import { JSONContent } from '@tiptap/react';
/**
 * 플레인 텍스트 + @[uid] 형태의 텍스트를 파싱하여 렌더링하는 함수
 */
interface ParsedContent {
    type: 'text' | 'mention';
    content: string;
    userId?: string;
}

export const parseMentions = (content: string): ParsedContent[] => {
    if (!content) return [];

    const regex = /@\[([\w-]+)\]/g;
    const parts: ParsedContent[] = [];

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(content)) !== null) {
        // 멘션 전 일반 텍스트
        if (match.index > lastIndex) {
            parts.push({
                type: 'text',
                content: content.substring(lastIndex, match.index),
            });
        }

        // 멘션
        const userId = match[1];
        parts.push({
            type: 'mention',
            content: match[0],
            userId,
        });

        lastIndex = match.index + match[0].length;
    }

    // 마지막 텍스트 부분
    if (lastIndex < content.length) {
        parts.push({
            type: 'text',
            content: content.substring(lastIndex),
        });
    }

    return parts;
};

/**
 * tiptap 에디터의 JSON 콘텐츠를 플레인 텍스트로 변환하고
 * 멘션 노드를 @[uid] 형태로 변환하는 함수
 * @param json tiptap 에디터의 JSON 콘텐츠
 * @returns {string} 변환된 플레인 텍스트
 */
export const convertJsonToPlainTextWithMentions = (
    json: JSONContent
): string => {
    if (!json?.content) return '';

    let result = '';

    // JSON 콘텐츠를 순회하며 텍스트 추출
    const processNode = (node: JSONContent): string => {
        let nodeText = '';

        // 노드 타입에 따라 처리
        switch (node.type) {
            case 'text':
                // 일반 텍스트 노드는 그대로 추가
                nodeText += node.text;
                break;

            case 'mention': {
                // 멘션 노드는 @[uid] 형태로 변환
                const userId = (node.attrs as { id: string })?.id;
                if (userId) {
                    nodeText += `@[${userId}]`;
                }
                break;
            }

            case 'paragraph':
                // 단락은 내용을 처리한 후 줄바꿈 추가
                if (node.content) {
                    node.content.forEach((childNode: JSONContent) => {
                        nodeText += processNode(childNode);
                    });
                    nodeText += '\n';
                } else {
                    nodeText += '\n';
                }
                break;

            // 다른 노드 타입들에 대한 처리 추가 가능
            case 'doc':
            case 'blockquote':
            case 'bulletList':
            case 'orderedList':
            case 'listItem':
            case 'heading':
            default:
                // 자식 노드가 있으면 재귀적으로 처리
                if (node.content) {
                    node.content.forEach((childNode: JSONContent) => {
                        nodeText += processNode(childNode);
                    });
                }
                break;
        }

        return nodeText;
    };

    // 최상위 doc 노드부터 처리 시작
    result = processNode(json);
    return result;
    // return result.replace(/\n+$/, '').replace(/\n{3,}/g, '\n\n');
};

/**
 * 플레인 텍스트와 @[uid] 형태의 멘션을 tiptap HTML로 변환하는 함수
 * @param text 변환할 플레인 텍스트 (@[uid] 형태의 멘션 포함)
 * @param userMap 사용자 ID를 키로 하고 사용자 정보를 값으로 하는 맵 객체
 * @returns tiptap에서 사용할 수 있는 HTML 문자열
 */
export const convertPlainTextToTiptapHtml = (
    text: string,
    userMap: Record<string, { id: string; name: string }>
): string => {
    // 텍스트가 없으면 빈 문자열 반환
    if (!text) {
        return '';
    }
    // 줄바꿈으로 텍스트를 분할하여 각 줄을 단락으로 처리
    const paragraphs = text.split('\n');

    // 각 단락을 HTML로 변환
    const paragraphsHtml = paragraphs.map(paragraphText => {
        // @[uid] 패턴을 찾아 텍스트를 분할
        const regex = /@\[([\w-]+)\]/g;

        // 멘션을 HTML로 치환
        const html = paragraphText.replace(regex, (match, userId: string) => {
            const userInfo = userMap[userId];

            if (userInfo) {
                // 멘션 HTML 형식으로 변환
                return `<span data-type="mention" data-id="${userInfo.id}" data-label="${userInfo.name}" class="mention">${userInfo.name}</span>`;
            }

            // 사용자 정보가 없으면 원본 텍스트 그대로 반환
            return match;
        });

        return `<p>${html || '<br>'}</p>`;
    });

    return paragraphsHtml.join('');
};

/**
 * 텍스트에서 멘션 마크업(@[uid])을 제거 - 알림 발송 시 사용
 */
export const removeMentionMarkup = (content: string): string => {
    if (!content) return '';

    // @[uid] 패턴을 제거
    return content.replace(/@\[[\w-]+\]/g, '').trim();
};
